/**
 * Parse a Goodreads or StoryGraph library export (CSV) into a normalised list
 * of books ready to become transmissions.
 *
 * Pure and side-effect-free: it only turns text into structured data. The
 * actual (careful, deduped, user-scoped) writing to the transmissions table
 * lives in `importTransmissions` in transmissionsService.ts. Keeping the two
 * apart means the parser can be reasoned about — and unit-checked — without any
 * risk of touching the database.
 */

export type ImportSource = 'goodreads' | 'storygraph';
export type ImportStatus = 'read' | 'reading' | 'want-to-read';

export interface ParsedImportBook {
  title: string;
  author: string;
  isbn?: string;
  status: ImportStatus;
  rating?: number; // 1–5, omitted when unrated
  publicationYear?: number;
}

export interface ParsedImport {
  source: ImportSource;
  books: ParsedImportBook[];
}

/**
 * Minimal RFC-4180 CSV reader. Hand-rolled rather than pulling in a dependency:
 * it must cope with the two things real exports throw at you — quoted fields
 * containing commas, and (in Goodreads reviews) newlines inside a quoted field.
 */
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  const s = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  let field = '';
  let row: string[] = [];
  let inQuotes = false;

  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (inQuotes) {
      if (c === '"') {
        if (s[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"') {
      inQuotes = true;
    } else if (c === ',') {
      row.push(field);
      field = '';
    } else if (c === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += c;
    }
  }
  // Trailing field / row (files without a final newline).
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

const norm = (s: string) => (s || '').trim().toLowerCase();

function mapStatus(raw: string): ImportStatus {
  const s = norm(raw);
  if (s === 'currently-reading' || s === 'reading' || s === 'currently reading') return 'reading';
  if (s === 'to-read' || s === 'want-to-read' || s === 'to read') return 'want-to-read';
  // 'read', 'did-not-finish' and blanks all count as part of the reading history.
  return 'read';
}

function cleanIsbn(raw?: string): string | undefined {
  if (!raw) return undefined;
  // Goodreads wraps ISBNs as ="9780439023528"; strip everything but digits/X.
  const s = raw.replace(/[^0-9Xx]/g, '').toUpperCase();
  return s.length === 10 || s.length === 13 ? s : undefined;
}

function parseYear(raw?: string): number | undefined {
  if (!raw) return undefined;
  const n = parseInt(raw.trim(), 10);
  if (!Number.isFinite(n)) return undefined;
  const max = new Date().getFullYear() + 1;
  return n > 0 && n <= max ? n : undefined;
}

function parseRating(raw?: string): number | undefined {
  if (!raw) return undefined;
  const n = parseFloat(raw.trim());
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return Math.max(1, Math.min(5, Math.round(n)));
}

/** Build a case-insensitive header→index lookup. */
function headerIndex(header: string[]): Map<string, number> {
  const m = new Map<string, number>();
  header.forEach((h, i) => m.set(norm(h), i));
  return m;
}

/**
 * Parse an export file. Returns null when the text is not a recognisable
 * Goodreads or StoryGraph CSV (so the UI can show a clear message rather than
 * importing garbage).
 */
export function parseLibraryExport(text: string): ParsedImport | null {
  const rows = parseCsv(text);
  if (rows.length < 2) return null;

  const header = rows[0];
  const idx = headerIndex(header);
  const has = (name: string) => idx.has(norm(name));

  const isGoodreads = has('Exclusive Shelf') || (has('Book Id') && has('Author l-f'));
  const isStoryGraph = has('Read Status') && (has('Authors') || has('Author'));

  if (!isGoodreads && !isStoryGraph) return null;

  const source: ImportSource = isGoodreads ? 'goodreads' : 'storygraph';
  const get = (row: string[], name: string): string => {
    const i = idx.get(norm(name));
    return i === undefined ? '' : (row[i] ?? '');
  };

  const books: ParsedImportBook[] = [];

  for (let r = 1; r < rows.length; r++) {
    const row = rows[r];
    if (!row || row.length === 0) continue;

    const title = get(row, 'Title').trim();
    if (!title) continue;

    let author: string;
    if (source === 'goodreads') {
      author = get(row, 'Author').trim();
    } else {
      // StoryGraph "Authors" can list several — the first is the primary.
      author = (get(row, 'Authors') || get(row, 'Author')).split(',')[0].trim();
    }
    if (!author) continue;

    const isbn = cleanIsbn(source === 'goodreads' ? get(row, 'ISBN13') || get(row, 'ISBN') : get(row, 'ISBN/UID'));

    const status = mapStatus(source === 'goodreads' ? get(row, 'Exclusive Shelf') : get(row, 'Read Status'));

    const rating = parseRating(source === 'goodreads' ? get(row, 'My Rating') : get(row, 'Star Rating'));

    const publicationYear =
      source === 'goodreads'
        ? parseYear(get(row, 'Original Publication Year') || get(row, 'Year Published'))
        : undefined;

    books.push({ title, author, isbn, status, rating, publicationYear });
  }

  return { source, books };
}
